// src/ai/fallbackHints.ts — Pre-Authored Socratic Fallback Hints & Failure Logging (T-5.2.2, FR-AI-003)
// Delivers guaranteed Socratic assistance when offline or when Gemini proxy is unreachable,
// logging incidents to local storage for parental review.

import type { HintRequest, HintResponse, AiFailureLog } from './tutorTypes';

const AI_FAILURES_KEY = 'clawey_ai_failures_v1';

export const DOMAIN_FALLBACK_HINTS: Record<string, Array<{ hint: string; prompt: string }>> = {
  comprehension: [
    {
      hint: 'Let us peer closely at the passage together through our naturalist magnifier. The author often plants a subtle clue in the adjectives surrounding the characters.',
      prompt: 'Which phrase in the text tells you how the character was feeling inside?',
    },
    {
      hint: 'Every true detective inspects the evidence sentence by sentence. Look for words that describe sounds or physical actions.',
      prompt: 'What clue in line two connects directly to your question?',
    },
  ],
  verbal: [
    {
      hint: 'Consider the family of words this specimen belongs to. If you substitute each option into a test sentence, one will ring true like a ship’s bell.',
      prompt: 'Which word creates the exact same meaning when you say it out loud?',
    },
    {
      hint: 'Notice the pattern in how the letters shift forward or backward along the alphabet tide. Count the steps between each letter pair.',
      prompt: 'How many letters forward does the first letter jump?',
    },
  ],
  numerical: [
    {
      hint: 'Break the problem down into two rockpool steps! First calculate the total quantity, then divide it into equal portions.',
      prompt: 'What is the result when you complete only the first step?',
    },
    {
      hint: 'Remember that fractions are like equal slices of a sea-sponge cake. Before adding or comparing, make sure the bottom denominators match perfectly.',
      prompt: 'What common multiple can both denominators share?',
    },
  ],
  nvr: [
    {
      hint: 'Watch how the shapes rotate like a ship’s compass needle. Observe whether the shading moves clockwise or flips like a mirror reflection.',
      prompt: 'Is the symbol turning by a quarter-turn (90°) or half-turn (180°)?',
    },
    {
      hint: 'Count the total number of lines, dots, or sides on each tile in the row. There is an orderly rule governing each change.',
      prompt: 'Does the count increase by one dot at each step across the grid?',
    },
  ],
  geometry: [
    {
      hint: 'Compare the angle against a square book corner of 90 degrees. If the jaws are sharper and narrower, it must measure less than 90 degrees.',
      prompt: 'Does this angle look acute, right, or obtuse to your naturalist eye?',
    },
    {
      hint: 'On a coordinate grid, remember Clawey’s golden claw rule: walk along the beach first (x-axis), then climb up the seaweed cliff (y-axis)!',
      prompt: 'Which axis number do you read first?',
    },
  ],
  spelling: [
    {
      hint: 'Clap out the syllables like waves crashing on the shore! Break the long word into smaller, friendly chunks.',
      prompt: 'What is the root word hiding inside the prefix and suffix?',
    },
  ],
};

export class FallbackHintSystem {
  /**
   * Retrieves a verified Socratic fallback hint tailored to the requested domain.
   */
  static getFallback(request: HintRequest): HintResponse {
    const domain = (request.domain || 'numerical').toLowerCase();
    const list = DOMAIN_FALLBACK_HINTS[domain] || DOMAIN_FALLBACK_HINTS['numerical']!;
    const entry = list[Math.floor(Math.random() * list.length)] || list[0]!;

    return {
      hint: entry.hint,
      interactivePrompt: entry.prompt,
      confidence: 0.85,
      source: 'fallback',
      thinkingSummary: `Resilient local fallback deployed for domain [${domain}].`,
    };
  }

  /**
   * Logs an AI proxy failure to localStorage for parental review on the Parent Dashboard (FR-AI-003).
   */
  static logFailure(request: HintRequest, error: string): void {
    try {
      const logs = this.getFailureLogs();
      const dateFormatted = new Date().toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });

      const entry: AiFailureLog = {
        timestamp: Date.now(),
        dateFormatted,
        questionId: request.questionId,
        domain: request.domain,
        errorMessage: error,
      };

      logs.unshift(entry);
      // Keep most recent 50 logs
      localStorage.setItem(AI_FAILURES_KEY, JSON.stringify(logs.slice(0, 50)));
    } catch {
      // Ignore quota exceptions
    }
  }

  /** Retrieve all logged AI failures */
  static getFailureLogs(): AiFailureLog[] {
    try {
      const raw = localStorage.getItem(AI_FAILURES_KEY);
      if (!raw) return [];
      const list = JSON.parse(raw);
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }

  /** Clear failure logs */
  static clearFailureLogs(): void {
    localStorage.removeItem(AI_FAILURES_KEY);
  }
}
